// EUME Accidental Care Plan — Terms & Conditions copy.
// Source of truth: "EUME Accidental Plan TnCs page" design export. Keep wording exact.

import {
  bullets,
  para,
  step,
  titled,
  type TncHero,
  type TncPreface,
  type TncSection,
} from "@/components/tnc/tncTypes";

export const eumeTncHero: TncHero = {
  breadcrumb: "EUME Accidental Care Plan",
  current: "Terms & Conditions",
  title: "Terms and Conditions",
  lines: [
    "Accidental Damage Protection Plan",
    "For eligible EUME products.",
  ],
};

export const eumeTncPreface: TncPreface = {
  title: "Welcome",
  paragraphs: [
    "Thank you for choosing EUME.",
    "To enhance your ownership experience and provide additional peace of mind, EUME offers an optional Accidental Damage Protection Plan (“ADP Plan”) administered by EmbedMike India Private Limited (“Protega”).",
    "The ADP Plan is designed to provide protection against all eligible accidental physical damage arising from unforeseen and unintentional events occurring during normal personal use of covered products.",
    "This document sets out the complete terms and conditions governing the Plan, including coverage scope, exclusions, eligibility requirements, claim procedures, limitations, customer responsibilities, and service commitments.",
    "Customers are advised to carefully review this document and retain it for future reference.",
  ],
};

export const eumeTncFooterNote =
  "EUME Accidental Care Plan is administered by EmbedMike India Private Limited (Protega) on behalf of EUME. It is a service contract, not an insurance policy, and the Terms and Conditions govern in full.";

export const eumeTncSections: TncSection[] = [
  {
    kind: "clauses",
    id: "plan-introduction",
    title: "Plan introduction",
    blocks: [
      titled(
        "About the Plan",
        "The EUME Accidental Damage Protection Plan (“Plan”) is an optional post-purchase protection service contract that may be purchased by customers at the time of checkout for eligible EUME products.",
        "The Plan is intended to provide protection against accidental physical damage that occurs during normal personal use of the covered product and that directly affects its functionality, usability, or structural integrity and also airline handling."
      ),
      titled(
        "Nature of the Plan",
        "This Plan is a service contract administered by EmbedMike India Private Limited (“Protega”) on behalf of EUME.",
        "The Plan:",
        {
          bullets: [
            "Is not an insurance policy.",
            "Is not an insurance product.",
            "Does not provide coverage against every form of damage or loss.",
            "Does not replace statutory rights available under applicable law.",
          ],
        },
        "The Plan provides protection only against the accidental physical damage events expressly described in these Terms and Conditions."
      ),
      titled(
        "Covered Products",
        "The Plan may be purchased for eligible EUME products including but not limited to: Luggage, Laptop Backpacks, Messenger Bags, Handbags, Travel Accessories.",
        "The Plan shall apply only to the specific product for which it has been purchased."
      ),
      titled(
        "Coverage Duration",
        "Customers may purchase either:",
        {
          bullets: [
            "**One (1) Year Plan:** Coverage remains valid for twelve (12) months from the date of activation.",
            "**Two (2) Year Plan:** Coverage remains valid for twenty-four (24) months from the date of activation.",
          ],
        },
        "The selected coverage duration shall be specified in the customer’s Plan confirmation."
      ),
      titled(
        "Commencement of Coverage",
        "Coverage shall commence on the later of:",
        {
          bullets: [
            "Successful delivery of the covered product; or",
            "Plan activation.",
          ],
        }
      ),
      titled(
        "Territorial Scope",
        "Coverage under this Plan shall apply only within India. The Plan may cover eligible incidents occurring within or outside India. However, all claim registration, inspection, verification, repair, replacement and other claim servicing under the Plan shall be carried out only within India in accordance with these Terms and Conditions."
      ),
      titled(
        "Claim Structure",
        "The Plan permits a maximum of one (1) approved claim per covered product during the selected Plan term. Upon successful settlement of an approved claim through repair, replacement, or Store Credit, the Plan shall automatically terminate. No additional claims shall be permitted thereafter."
      ),
      titled(
        "Resolution Hierarchy",
        "Approved claims may be resolved through Repair, Replacement, or Store Credit (where applicable). The selected resolution method shall be determined solely by Protega based upon the nature of the damage, product condition, serviceability, spare-part availability, and operational feasibility."
      ),
      titled(
        "Deductible",
        "Each approved claim under this Plan shall be subject to a deductible payable by the customer. The deductible shall be equal to the lower of:",
        {
          bullets: [
            "Ten percent (10%) of the original invoice value of the covered product; or",
            "₹1,000 per approved claim.",
          ],
        },
        "The deductible shall be payable prior to repair, replacement, or settlement of the approved claim. The deductible is intended to represent the customer’s contribution towards the cost of claim fulfilment and shall apply only to approved claims. No deductible shall be payable where a claim is declined or determined to be outside the scope of coverage."
      ),
      titled(
        "Administration",
        "All claim registration, verification, assessment, customer communication, repair coordination, replacement authorization, and settlement administration shall be managed by Protega. All decisions relating to coverage eligibility, claim approval, repairability, replacement eligibility, and settlement value shall be made by Protega in accordance with these Terms and Conditions."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "customer-responsibilities",
    title: "Customer responsibilities",
    blocks: [
      para(
        "To remain eligible for coverage under this Plan, customers are required to exercise reasonable care in the use, handling, storage, and maintenance of the covered product and comply with the obligations set forth in this section."
      ),
      bullets(
        "The customer shall retain the purchase invoice (including an e-invoice, where applicable), order confirmation, and Plan confirmation issued at the time of purchase. These documents may be required during claim assessment and verification.",
        "The customer shall report any accidental damage incident as soon as reasonably possible and no later than seventy-two (72) hours from the date of occurrence or discovery of the damage. Delayed reporting may affect Protega’s ability to assess the claim and may result in claim rejection where the cause of damage cannot be reasonably established.",
        "Customers must provide complete, accurate, and truthful information regarding the circumstances of the incident and shall not intentionally conceal, misrepresent, or omit material facts relevant to the claim.",
        "Where requested, customers shall provide photographs, videos, invoices, travel documentation, airline reports, baggage tags, inspection reports, or any other supporting documentation reasonably required by Protega to validate the claim.",
        "The customer shall take reasonable steps to prevent further damage after an incident occurs. Continued use of a severely damaged product that results in additional deterioration may affect claim eligibility.",
        "The covered product must not be repaired, altered, dismantled, modified, or serviced by any unauthorized repair provider without prior approval from Protega.",
        "Customers shall cooperate fully throughout the claim assessment, inspection, pickup, repair, replacement, and settlement process."
      ),
      para(
        "Failure to comply with these responsibilities may result in claim denial or cancellation of coverage."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "how-to-file-a-claim",
    title: "How to file a claim",
    blocks: [
      step("Step 1 – Claim registration"),
      para(
        "All claims must be registered directly through Protega’s official customer portal or Protega’s WhatsApp number:"
      ),
      bullets(
        "Customer Portal: www.protega.ai/customer-login",
        "WhatsApp: +91 93240 95283"
      ),
      para(
        "Claims submitted solely through social media platforms, marketplaces, third-party sellers, courier partners, or informal communication channels shall not constitute valid claim registration under this Plan. Customers may contact EUME customer support for assistance; however, all claims must ultimately be registered through Protega’s designated claims channels."
      ),
      step("Step 2 – Submission of documentation"),
      para(
        "Customers may be required to submit documentation including but not limited to:"
      ),
      bullets(
        "Purchase invoice",
        "Plan confirmation",
        "Product photographs",
        "Images or videos showing the damage",
        "Description of the accidental incident",
        "Product serial number or identification details, where applicable",
        "Airline baggage reports (if applicable)",
        "Baggage tags (if applicable)",
        "Delivery records (if applicable)",
        "Any additional documentation reasonably requested by Protega"
      ),
      para(
        "Protega reserves the right to request additional information where necessary to determine coverage eligibility."
      ),
      step("Step 3 – Claim assessment"),
      para(
        "Upon receipt of the required documentation, Protega shall conduct an assessment to determine: Product eligibility, Plan validity, Nature and cause of damage, Coverage applicability, Repair feasibility, Replacement eligibility, Compliance with Plan terms and conditions. Standard assessments are generally completed within twenty-four (24) to forty-eight (48) working hours following receipt of complete documentation."
      ),
      step("Step 4 – Resolution"),
      para(
        "If the claim is approved, Protega may arrange one of the following resolutions: Repair, Replacement, Store Credit (where applicable). The selected resolution method shall be determined solely by Protega based upon technical assessment and operational feasibility."
      ),
      step("Step 5 – Claim closure"),
      para(
        "Upon completion of the approved resolution, the claim shall be considered settled and closed. Since the Plan permits only one approved claim per covered product, coverage shall automatically terminate upon completion of claim settlement."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "definitions",
    title: "Definitions",
    blocks: [
      para("For the purposes of this Plan, the following definitions shall apply:"),
      bullets(
        "“**Accidental Damage**” means sudden, unforeseen, unintentional, and externally caused physical damage that directly affects the functionality, usability, or structural integrity of the covered product.",
        "“**Covered Product**” means an eligible EUME product enrolled under this Plan.",
        "“**Administrator**” means EmbedMike India Private Limited (“Protega”).",
        "“**Customer**” means the original purchaser of the covered product and the Plan.",
        "“**Repair**” means restoration of the covered product through servicing, replacement of damaged components, or corrective measures intended to restore functionality.",
        "“**Replacement**” means provision of a new, refurbished, repaired, or equivalent product in lieu of the damaged product.",
        "“**Store Credit**” means a non-cash settlement issued in the form of EUME store credit, voucher, coupon code, wallet credit, or any other non-cash settlement mechanism determined by Protega and EUME.",
        "“**Beyond Economic Repair (BER)**” means a condition where repair is determined to be commercially unreasonable or technically practical.",
        "“**Invoice Value**” means the original purchase value of the covered product as reflected on the customer’s valid invoice.",
        "“**Plan Term**” means the duration of coverage selected by the customer at the time of Plan purchase.",
        "“**Authorized Service Partner**” means a repair provider, service center, inspection partner, logistics partner, or fulfillment provider authorized by Protega."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "eligibility",
    title: "Eligibility",
    blocks: [
      para(
        "Coverage under this Plan shall apply only when all eligibility requirements described herein are satisfied."
      ),
      bullets(
        "The covered product must have been purchased through an authorized EUME sales channel and must be enrolled under the Accidental Damage Protection Plan during the permitted purchase window.",
        "The product must be new and free from known accidental damage at the time of Plan activation.",
        "Coverage shall apply only to products used for personal, non-commercial purposes. Products used for rental operations, commercial fleets, institutional deployment, industrial usage, or business operations may be excluded from coverage.",
        "The customer must possess a valid purchase invoice and Plan confirmation.",
        "Coverage shall remain valid only while the Plan remains active and within the applicable coverage period.",
        "The covered product must be physically located within India at the time of claim registration and service fulfillment.",
        "Eligibility shall be determined solely by Protega following review of the available information and supporting documentation."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-covered",
    title: "What is covered",
    blocks: [
      para(
        "Subject to these Terms and Conditions, the Plan provides coverage for accidental physical damage occurring during normal personal use of the covered product that directly affects its functionality, usability, or structural integrity. Coverage is intended to protect customers against sudden and unforeseen incidents that result in accidental damage and is not intended to function as a maintenance service contract or replacement for proper product care."
      ),
      para(
        "To qualify for coverage, the damage must be accidental in nature, externally caused, visible, verifiable, and directly attributable to a specific incident. Coverage may include, but is not limited to, the following situations:"
      ),
      titled(
        "Accidental Impact Damage",
        "Coverage includes accidental physical damage resulting from the following sudden impacts occurring during normal ownership and use of the covered product: accidental drops, accidental falls, accidental collisions, impact with external objects, travel-related accidental impacts, accidental crushing events, provided that such incidents result in functional or structural impairment of the covered product."
      ),
      titled(
        "Wheel Damage",
        "Coverage may apply to accidental wheel breakage, wheel detachment, wheel cracking, wheel housing damage, or wheel failure caused by accidental external impact. Coverage shall not apply where the damage arises solely from normal wear and tear, gradual deterioration, excessive usage, or ordinary aging."
      ),
      titled(
        "Trolley System Damage",
        "Coverage includes accidental damage to trolley handles, telescopic systems, extension mechanisms, locking systems, or associated structural components resulting from accidental external events. Coverage applies only where the damage arises from accidental causes and not manufacturing defects or ordinary wear."
      ),
      titled(
        "Zipper Damage",
        "Coverage includes accidental zipper damage that directly affects usability, including accidental breakage resulting from sudden external force, accidental snagging events, or accidental impact. Coverage does not extend to ordinary zipper wear, aging, stretching, or gradual deterioration."
      ),
      titled(
        "TSA Lock Damage",
        "Coverage includes accidental damage to integrated TSA locks and associated locking mechanisms where the damage results directly from an accidental event."
      ),
      titled(
        "Shell Damage",
        "For luggage products, coverage includes accidental cracking, breakage, puncturing, crushing, or structural damage to the outer shell caused by accidental external impact. Coverage shall apply only where the damage affects usability, protection, or structural integrity."
      ),
      titled(
        "Fabric and Structural Damage",
        "For backpacks, messenger bags, handbags, and similar products, coverage includes accidental tearing, puncturing, structural damage, or accidental breakage affecting usability. Coverage shall apply only where the damage is caused by a sudden accidental event and not by gradual wear or repeated usage."
      ),
      titled(
        "Liquid Damage",
        "Coverage may apply to accidental liquid exposure resulting from sudden and unintended incidents that directly impair the functionality or usability of the covered product. Coverage shall not apply to gradual moisture exposure, mold, mildew, environmental deterioration, or long-term neglect."
      ),
      titled(
        "Airline Handling Damage",
        "Subject to verification and supporting documentation, coverage may be available for accidental physical damage occurring during airline baggage handling.",
        "Customers may be required to provide: Airline baggage reports, Baggage claim documentation, Boarding records, Baggage tags, Photographs of the damage, Any additional supporting evidence reasonably requested by Protega.",
        "Customers should report airline baggage damage to the airline at the earliest available opportunity and retain all baggage irregularity reports, claim reference numbers, baggage tags, correspondence, and supporting documentation relating to the incident. Airline handling damage should be reported to Protega within seventy-two (72) hours of discovery. Failure to provide reasonable supporting documentation may affect claim assessment and eligibility.",
        "Coverage shall remain subject to all other terms, conditions, exclusions, and limitations contained in this Plan."
      ),
      titled(
        "Beyond Economic Repair",
        "Where accidental damage renders the covered product Beyond Economic Repair (“BER”), Protega may, at its sole discretion, provide: Replacement; or Store Credit (where applicable) as settlement of the approved claim. All decisions regarding repairability and BER status shall be made solely by Protega."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "what-is-not-covered",
    title: "What is not covered",
    blocks: [
      para(
        "This Plan is intended to cover accidental physical damage only. The following events, circumstances, losses, and conditions are specifically excluded from coverage."
      ),
      titled(
        "Manufacturing Defects",
        "Manufacturing defects, workmanship issues, assembly defects, material defects, and product quality concerns are not covered under this Plan."
      ),
      titled(
        "Theft, Loss and Disappearance",
        "The Plan does not cover: Theft, Burglary, Robbery, Misplacement, Unexplained disappearance, Missing products, Abandonment, Confiscation, Seizure. Coverage applies only to accidental physical damage and not to loss of possession."
      ),
      titled(
        "Cosmetic Damage",
        "The Plan does not cover purely cosmetic issues that do not impair functionality. Examples include: Scratches, Scuffs, Surface abrasions, Minor dents, Paint wear, Discoloration, Aesthetic imperfections, Surface marks, unless such damage directly impacts functionality or structural integrity which shall be decided by Protega at its sole discretion."
      ),
      titled(
        "Normal Wear and Tear",
        "The Plan does not cover deterioration arising through ordinary usage. Examples include: Wheel wear, Handle wear, Zipper wear, Fabric wear, Lock wear, Coating deterioration, Material aging, Surface fading, Ordinary product aging."
      ),
      titled(
        "Intentional Damage",
        "Any damage caused intentionally or arising from deliberate acts is excluded."
      ),
      titled(
        "Misuse and Negligence",
        "Coverage shall not apply where damage results directly or indirectly from: Abuse, Gross negligence, Reckless handling, Improper storage, Improper transportation, Overloading, Commercial misuse, Use inconsistent with intended purpose."
      ),
      titled(
        "Unauthorized Repairs and Alterations",
        "Coverage shall not apply where the product has been without authorisation: Modified, Altered, Repaired without authorization, Disassembled, Tampered with, Serviced by unauthorized providers."
      ),
      titled(
        "Commercial and Rental Usage",
        "Products used for: Rental purposes, Fleet operations, Commercial deployment, Institutional use, Industrial applications may be excluded from coverage."
      ),
      titled(
        "Natural Calamities and Force Majeure Events",
        "Coverage does not extend to damage arising from: Flood, Earthquake, Cyclone, Storm, Lightning, Fire, Riot, Civil unrest, War, Terrorism, Nuclear events, Government action or similar force majeure events."
      ),
      titled(
        "Pre-Existing Damage",
        "Damage that existed before Plan activation is not covered."
      ),
      titled(
        "Fraudulent Claims",
        "Claims involving false information, manipulated evidence, altered invoices, fabricated incidents, or concealment of material facts shall not be covered and may result in immediate termination of benefits."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "service-procedures",
    title: "Service procedures and limit of liability",
    blocks: [
      para(
        "All claims submitted under this Plan shall undergo review, assessment, and verification by Protega. Protega reserves the right to request inspection, photographs, videos, documentation, physical examination, or pickup of the covered product before determining claim eligibility."
      ),
      titled(
        "Resolution Framework",
        "Approved claims may be resolved through one of the following methods:"
      ),
      titled(
        "Deductible Requirement",
        "As a condition of claim settlement, the customer shall be required to pay the applicable deductible specified under this Plan. Repair, replacement, or Store Credit shall be processed only after receipt of the applicable deductible amount. Failure to pay the deductible within the timeframe communicated by Protega may result in closure of the approved claim request. The deductible shall not be refundable once claim settlement has commenced."
      ),
      titled(
        "Repair",
        "Where feasible, Protega may repair the covered product using original, compatible, refurbished, or equivalent components."
      ),
      titled(
        "Replacement",
        "Where repair is not practical or commercially viable, Protega may provide a replacement product. Replacement products may be: New, Refurbished, Repaired, Equivalent products of similar specifications and value. Exact matching of model, colour, design, collection, finish, or SKU cannot be guaranteed."
      ),
      titled(
        "Store Credit",
        "Where repair or replacement is not commercially or operationally feasible, Protega may issue Store Credit as settlement of the approved claim. Store Credit shall constitute full and final settlement of the claim."
      ),
      titled(
        "One Claim Limitation",
        "Only one (1) approved claim shall be permitted per covered product during the Plan term. Upon settlement through repair, replacement, or Store Credit, coverage shall automatically terminate."
      ),
      titled(
        "Aggregate Liability",
        "Protega’s maximum cumulative liability under this Plan shall not exceed the original invoice value of the covered product. In determining cumulative liability, Protega may consider: Repair costs, Logistics costs, Replacement value, Store Credits issued, Inspection expenses, Service fulfillment costs, Any other benefits provided under the Plan."
      ),
      titled(
        "No Cash Settlement",
        "The Plan does not provide: Cash reimbursement, Cash compensation, Monetary payouts, Cash settlements. Benefits under the Plan shall be limited solely to repair, replacement, or Store Credit as determined by Protega at its sole discretion."
      ),
      titled(
        "Final Determination",
        "All decisions relating to: Coverage eligibility, Cause of damage, Repairability, Replacement eligibility, Store Credit value, Claim approval shall be made by Protega and shall be final and binding."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "cancellation",
    title: "Cancellation",
    blocks: [
      titled(
        "Free-Look Cancellation",
        "Customers may cancel the Plan within seven (7) days from the date of purchase, provided that: No claim has been registered; No benefit has been availed under the Plan; No service request is under review or assessment. Where cancellation is approved during the free-look period, the customer shall be entitled to a full refund of the Plan fee."
      ),
      titled(
        "Cancellation After Free-Look Period",
        "Following expiry of the free-look period, the Plan shall become non-cancellable and non-refundable."
      ),
      titled(
        "Cancellation Due to Product Return",
        "Where the covered product is returned to EUME and the underlying purchase transaction is cancelled or refunded in accordance with EUME’s applicable return policy, the Plan shall automatically terminate. Provided no claim has been registered, approved, settled, or is under review, the customer shall be entitled to a full refund of the Plan fee. No refund of the Plan fee shall be payable where a claim has been registered, approved, settled, or is under active assessment."
      ),
      titled(
        "Fraudulent Activity",
        "Protega reserves the right to suspend, terminate, or cancel coverage immediately where there is evidence of: Fraud; Misrepresentation; False documentation; Altered invoices; Manipulated evidence; Warranty abuse; Intentional concealment of material information. Termination of coverage shall not prejudice any legal rights available to Protega under applicable law. Any amount paid towards the Protection Plan shall stand forfeited upon termination of coverage under this Clause."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "conditions",
    title: "Conditions",
    blocks: [
      titled(
        "Non-Transferability",
        "The Plan is issued solely for the benefit of the original purchaser and is non-transferable. The Plan may not be sold, assigned, transferred, gifted, or otherwise conveyed to any other individual or entity."
      ),
      titled(
        "Territorial Scope",
        "Coverage under the Plan extends to eligible incidents occurring within or outside India, subject to these Terms and Conditions."
      ),
      titled(
        "Product Ownership",
        "The customer must retain ownership of the covered product throughout the Plan term. Protega may request proof of ownership at any stage of the claims process."
      ),
      titled(
        "Inspection Rights",
        "Protega reserves the right to request: Product photographs; Videos; Inspection reports; Physical inspection; Product pickup; Additional documentation; before making a coverage determination. Failure to cooperate may result in claim rejection."
      ),
      titled(
        "Serviceability",
        "All services remain subject to: Geographic serviceability; Logistics availability; Spare-part availability; Vendor capacity; Operational feasibility. Certain remote locations may require additional processing time mentioned in the SLA."
      ),
      titled(
        "Product Replacement Conditions",
        "Where replacement is approved: The replacement product may be new, refurbished, repaired, or equivalent. Exact colour, model, collection, finish, or design matching cannot be guaranteed. Replacement shall satisfy the functional obligations of the Plan."
      ),
      titled(
        "Store Credit Conditions",
        "Store Credit may be issued where repair or replacement is not feasible at the discretion of Protega. Store Credit: Shall not be redeemable for cash; Shall not constitute monetary compensation; Shall be subject to the terms communicated at the time of issuance."
      ),
      titled(
        "Salvage Rights",
        "Upon replacement or Store Credit settlement, Protega may require surrender of the damaged product. Ownership of the surrendered product shall transfer to Protega or its designated service partner."
      ),
      titled(
        "Force Majeure",
        "Protega shall not be liable for delays or inability to provide services arising from circumstances beyond its reasonable control, including but not limited to: Natural disasters; Government restrictions; Transportation disruptions; Strikes; Pandemic or epidemics; Vendor failures; Supply chain disruptions."
      ),
    ],
  },
  {
    kind: "clauses",
    id: "legal-disclosures",
    title: "Legal disclosures",
    blocks: [
      titled(
        "Governing Law",
        "This Plan shall be governed by and interpreted in accordance with the laws of India."
      ),
      titled(
        "Arbitration",
        "Any dispute, controversy, or claim arising out of or relating to this Plan shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996. The arbitration shall be conducted in English. The venue of arbitration shall be Mumbai, Maharashtra. The decision of the arbitrator shall be final and binding upon all parties."
      ),
      titled(
        "Limitation of Liability",
        "To the maximum extent permitted by law, Protega and EUME shall not be liable for: Indirect losses; Consequential losses; Incidental losses; Special damages; Punitive damages; Loss of business; Loss of profits; Loss of opportunity; Travel expenses; Emotional distress; Inconvenience; Loss of use. The customer’s sole remedy shall be limited to the benefits expressly described in this Plan."
      ),
      titled(
        "Entire Agreement",
        "These Terms and Conditions, together with the Plan confirmation issued to the customer, constitute the entire agreement governing the Plan."
      ),
      titled(
        "Severability",
        "If any provision of this Plan is determined to be invalid or unenforceable, the remaining provisions shall remain valid and enforceable."
      ),
      titled(
        "Data Processing",
        "Protega may collect, store, process, and use customer information for: Claims administration; Customer support; Fraud prevention; Service delivery; Regulatory compliance; Service contract improvement. Such processing shall be carried out in accordance with applicable laws and Protega’s privacy practices."
      ),
      titled(
        "No Insurance",
        "This Plan is a service contract. It is not: An insurance policy; An insurance product; A financial guarantee; A guarantee against all forms of product damage. Coverage is strictly limited to the benefits expressly described in this document."
      ),
    ],
  },
  {
    kind: "faq",
    id: "faq",
    title: "FAQ",
    items: [
      {
        q: "What does the Accidental Damage Protection Plan cover?",
        a: "The Plan covers eligible accidental physical damage that affects the usability, functionality, or structural integrity of the covered product.",
      },
      {
        q: "Does the Plan cover manufacturing defects?",
        a: "No. Manufacturing defects are not covered under this Plan.",
      },
      {
        q: "How many claims are allowed?",
        a: "The Plan permits one (1) approved claim per covered product during the selected Plan term.",
      },
      {
        q: "Does the Plan cover airline baggage damage?",
        a: "Yes. Subject to verification, airline baggage handling damage may be covered if the damage affects the functionality or structural integrity of the covered product.",
      },
      {
        q: "Does the Plan cover theft or loss?",
        a: "No. Loss, theft, disappearance, burglary, robbery, and missing products are specifically excluded.",
      },
      {
        q: "Is there a deductible?",
        a: "Yes. Each approved claim is subject to a deductible equal to the lower of: 10% of the original invoice value of the covered product; or ₹1,000. The deductible must be paid before repair, replacement, or Store Credit is processed.",
      },
      {
        q: "What happens if repair is not possible?",
        a: "Protega may provide: Replacement; or Store Credit (where applicable) depending on the circumstances of the claim.",
      },
      {
        q: "Can I receive cash compensation?",
        a: "No. The Plan does not provide cash reimbursement, cash settlement, or monetary compensation.",
      },
      {
        q: "What documents may be required?",
        a: "Depending on the nature of the claim, customers may be asked to provide: Invoice; Plan confirmation; Product photographs; Videos; Airline documentation; Baggage reports; Supporting evidence relating to the incident.",
      },
      {
        q: "Can I transfer my Plan to someone else?",
        a: "No. The Plan is valid only for the original purchaser.",
      },
      {
        q: "What if the product is damaged before Plan activation?",
        a: "Pre-existing damage is not covered.",
      },
      {
        q: "Can I cancel the Plan?",
        a: "Yes. Cancellation is available within the seven (7) day free-look period, subject to the terms set out in Section 9.",
      },
      {
        q: "What happens if I return my EUME product?",
        a: "If the covered product is returned and the underlying purchase transaction is cancelled or refunded in accordance with EUME’s return policy, the Plan shall automatically terminate. Provided no claim has been registered, approved, settled, or is under review, the customer shall be entitled to a refund of the Plan fee.",
      },
    ],
  },
  {
    kind: "clauses",
    id: "service-commitment",
    title: "Service commitment",
    blocks: [
      para(
        "Protega is committed to delivering a transparent, efficient, and customer-friendly claims experience. Indicative service timelines are as follows:"
      ),
      {
        kind: "table",
        rows: [
          ["Claim acknowledgement", "Within 24 working hours"],
          ["Initial assessment", "24–48 working hours"],
          ["Standard repair cases", "Typically 5–7 working days"],
          ["Replacement cases", "Subject to product availability"],
          ["Complex investigations", "Subject to operational review"],
          ["Airline damage review", "Typically 24–72 working hours"],
        ],
      },
      step("Customer communication"),
      para(
        "Customers shall receive regular updates throughout the claim lifecycle through: Email; Phone; WhatsApp; Customer Dashboard notifications."
      ),
      step("Service quality"),
      para(
        "Protega continuously monitors: Resolution timelines; Vendor performance; Customer satisfaction; Service quality metrics; to ensure a consistent customer experience."
      ),
    ],
  },
  {
    kind: "contact",
    id: "contact-us",
    title: "Contact us",
    intro:
      "For assistance or support, customers may contact Protega Customer Support. Claims can be registered only through the customer portal or WhatsApp.",
    items: [
      {
        label: "Email",
        value: "support@protega.ai",
        href: "mailto:support@protega.ai",
      },
      {
        label: "Web",
        value: "www.protega.ai/customer-login",
        href: "https://www.protega.ai/customer-login",
      },
      {
        label: "WhatsApp",
        value: "+91 93240 95283",
        href: "https://wa.me/919324095283",
      },
    ],
    after: [
      para(
        "Administrator: EmbedMike India Private Limited (“Protega”), administrator of the EUME Accidental Damage Protection Service contract."
      ),
      step("Important notice"),
      para(
        "This Plan covers accidental physical damage only and does not cover manufacturing defects, theft, loss, disappearance, normal wear and tear, cosmetic deterioration, misuse, abuse, intentional damage, unauthorized modifications, or excluded events described in these Terms and Conditions."
      ),
      para(
        "Submission of a claim does not guarantee approval. All claims remain subject to review, verification, assessment, and eligibility determination by Protega in accordance with the terms of this Plan."
      ),
    ],
  },
];
